import arrayMove from 'array-move';
import * as React from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  closestCenter,
  DragOverlay,
  defaultDropAnimationSideEffects,
  DropAnimation,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { DragHandleProvider } from '../../hooks/useDragHandle';

/**
 * Drop animation configuration.
 */
const dropAnimationConfig: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.4',
      },
    },
  }),
};

/**
 * Drag overlay to mirror the active (dragged) item.
 *
 * inPortal renders the preview into <body> (ancestor overflow/transform mis-position the
 * fixed DragOverlay); wrapper rebuilds the ancestor DOM its item styling is scoped to.
 */
const SortableOverlay = ({ children, inPortal = false, wrapper = undefined }) => {
  const overlay = (
    <DragOverlay className={inPortal ? 'sortable-drag-overlay' : undefined} dropAnimation={dropAnimationConfig}>
      {wrapper ? wrapper(children) : children}
    </DragOverlay>
  );
  return inPortal ? createPortal(overlay, document.body) : overlay;
};

/**
 * Custom keyboard sensor that respects certain element types and uses Enter or Space to activate.
 */
export const KeyboardSensorThatHandlesElementType = Object.assign(
  function KeyboardSensorThatHandlesElementType(props: ConstructorParameters<typeof KeyboardSensor>[0]) {
    return new KeyboardSensor(props);
  },
  { activators: [
    {
      eventName: 'onKeyDown',
      handler: ({ nativeEvent: event }: React.KeyboardEvent<Element>) => {
        if (!shouldHandleEvent(event)) {
          return false;
        }
        return event.key === 'Enter' || event.key === ' ';
      },
    },
  ] }
) as unknown as typeof KeyboardSensor;

/**
 * Custom pointer sensor that respects certain element types.
 */
export const PointerSensorThatHandlesElementType = Object.assign(
  function PointerSensorThatHandlesElementType(props: ConstructorParameters<typeof PointerSensor>[0]) {
    return new PointerSensor(props);
  },
  { activators: [
    {
      eventName: 'onPointerDown',
      handler: ({ nativeEvent: event }: React.PointerEvent<Element>) =>
        shouldHandleEvent(event),
    },
  ] }
) as unknown as typeof PointerSensor;

/**
 * Helper function to determine whether the event should be handled.
 */
function shouldHandleEvent(event: React.SyntheticEvent) {
  if ((event as React.KeyboardEvent).key === 'Tab') {
    return false;
  }
  const target = event.target;

  return !(target instanceof Element && target.closest('input, select, textarea, button, [contenteditable="true"]'));
}

/**
 * A helper component that wraps (clones) an existing element with sortable functionality.
 */
const SortableItem = ({
  helperClass = '',
  id,
  itemElement,
  order,
  useDragHandle = false,
  disabled = false,
  onDisabledInteraction = undefined,
}) => {
  const customAccessibleLabel = itemElement.props['data-sortable-label'];
  const accessibleLabel = customAccessibleLabel || `Row ${order}`;
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ data: { accessibleLabel }, id });

  const activeListeners = disabled
    ? {
      onPointerDown: (e: React.PointerEvent) => { e.preventDefault(); onDisabledInteraction?.(); },
      onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onDisabledInteraction?.(); } },
    }
    : listeners;
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const tabIndex =
    itemElement.props.tabIndex !== undefined ? itemElement.props.tabIndex : 0;

  if (useDragHandle) {
    // When using a drag handle, the row should be completely transparent to AT
    // Only the drag handle is interactive for reordering
    const dragHandleAttributes = {
      ...attributes,
      'aria-disabled': disabled || undefined,
      'aria-label': customAccessibleLabel ? `Reorder ${accessibleLabel}` : `Reorder row ${order}`,
      'aria-roledescription': 'sortable',
      role: 'button',
      tabIndex: 0,
    };

    // Row gets minimal attributes - no role, no tabIndex, no aria-label
    // Content inside should be read and interacted with normally
    const clonedElement = React.cloneElement(itemElement, {
      'data-id': id,
      className: `${itemElement.props.className || ''} ${helperClass}`.trim(),
      ref: setNodeRef,
      style: { ...(itemElement.props.style || {}), ...style },
    });

    return (
      <DragHandleProvider
        attributes={dragHandleAttributes}
        listeners={activeListeners}
      >
        {clonedElement}
      </DragHandleProvider>
    );
  }

  // When not using a drag handle, the whole row is draggable and interactive
  const mergedAttributes = {
    ...attributes,
    'aria-disabled': disabled || undefined,
    'aria-label': `Row ${order}`,
    'aria-roledescription': 'sortable',
    role: attributes.role,
  };

  const clonedElement = React.cloneElement(itemElement, {
    'data-id': id,
    className: `${itemElement.props.className || ''} ${helperClass}`.trim(),
    ref: setNodeRef,
    style: { ...(itemElement.props.style || {}), ...style },
    tabIndex,
    ...activeListeners,
    ...mergedAttributes,
  });

  return clonedElement;
};

/**
 * The main SortableList component.
 */
export const SortableList = ({
  containerElement = null,
  data,
  helperClass,
  renderOverlayItem = null,
  sortCallback,
  useDragHandle,
  disabled = false,
  onDisabledInteraction = undefined,
  overlayInPortal = false,
  overlayWrapper = undefined,
}) => {
  const [activeId, setActiveId] = React.useState<string | null>(null);

  const pointerSensor = useSensor(PointerSensorThatHandlesElementType, {
    activationConstraint: { distance: 8 },
  });
  const keyboardSensor = useSensor(KeyboardSensorThatHandlesElementType, {
    coordinateGetter: sortableKeyboardCoordinates,
  });
  const sensors = useSensors(pointerSensor, keyboardSensor);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      const oldIndex = data.findIndex(
        (item) => String(item.key) === String(active.id)
      );
      const newIndex = data.findIndex(
        (item) => String(item.key) === String(over.id)
      );
      const newData = arrayMove(data, oldIndex, newIndex);
      sortCallback(newData);
      setTimeout(() => {
        const movedElement = document.querySelector<HTMLElement>(
          `[data-id=\"${active.id}\"]`
        );
        const dragHandle = movedElement?.querySelector<HTMLElement>(
          '[aria-roledescription="sortable"]'
        );
        (dragHandle || movedElement)?.focus();
      });
    }
    setActiveId(null);
  };

  const handleDragStart = ({ active }) => {
    setActiveId(active.id);
  };

  const sortableItems = data.map((item, i) => (
    <SortableItem
      disabled={disabled}
      helperClass={helperClass}
      id={item.key}
      itemElement={item}
      key={String(item.key)}
      onDisabledInteraction={onDisabledInteraction}
      order={i + 1}
      useDragHandle={useDragHandle}
    />
  ));

  const renderActiveItem = () => {
    const index = data.findIndex((item) => String(item.key) === String(activeId));
    const itemElement = data[index];
    if (!itemElement) { return null }
    if (renderOverlayItem) { return renderOverlayItem(itemElement) }

    return sortableItems[index];
  };

  return (
    <DndContext
      accessibility={{
        announcements: {
          onDragCancel: ({ active }) =>
            `Drag cancelled for ${active.data.current?.accessibleLabel}`,
          onDragEnd: ({ active, over }) =>
            over
              ? `Dropped ${active.data.current?.accessibleLabel} over ${over.data.current?.accessibleLabel || over.id}`
              : `Dropped ${active.data.current?.accessibleLabel}`,
          onDragOver: ({ active }) =>
            `${active.data.current?.accessibleLabel} currently being dragged`,
          onDragStart: ({ active }) =>
            `Picked up ${active.data.current?.accessibleLabel}`,
        },
      }}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      sensors={sensors}
    >
      <SortableContext
        items={data.map((item) => item.key)}
        strategy={verticalListSortingStrategy}
      >
        {containerElement
          ? React.cloneElement(containerElement, undefined, sortableItems)
          : sortableItems}
      </SortableContext>
      <SortableOverlay inPortal={overlayInPortal} wrapper={overlayWrapper}>
        {activeId ? renderActiveItem() : null}
      </SortableOverlay>
    </DndContext>
  );
};
